import React from 'react'
import clsx from 'clsx'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import {
    ApplyBand,
    buttonSx,
    classToFComponent,
    CollapsedPaneFilterIcon,
    CollapsedPaneHeader,
    CollapsedPaneLabel,
    FCClassContext,
    Field,
    FieldLabel,
    FilterPaneContainer,
    FilterPaneHeader,
    IconActionButton,
    inputSx,
    PaneTitle
} from 'src/utils/views'

import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'

import FilterCatalogView from '../../the-consumer-market/views/filters/tcm_filter_catalog'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'

import {
    DateFilterScope,
    MultipleOptionStringKeywordFilterScope,
    NumberFilterScope,
    SelectFilterScope,
    StringFilterScope,
    StringKeywordFilterScope,
    type IFilterScope
} from '../../the-consumer-market/tcm_scopes'
import DateFilterView from '../../the-consumer-market/views/filters/tcm_filter_for_date'
import NumberFilterView from '../../the-consumer-market/views/filters/tcm_filter_for_number'
import TcmStringFilterView from '../../the-consumer-market/views/filters/tcm_filter_for_string'
import TcmStringKeywordFilterView from '../../the-consumer-market/views/filters/tcm_filter_for_string_keyword'
import StringMultiSelectFilterView from '../../the-consumer-market/views/filters/tcm_filter_for_string_multi_select'
import { SelectFilterView } from '../../the-consumer-market/views/filters/tcm_filter_select'
import { MvFilterPaneScope } from '../mv_scopes'

const LOG = Logger.get('MV-FilterPane')

const FilterBody = styled('div')({
    padding: '16px 14px 4px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    '& > *': { flexShrink: 0 }
})

const useStyles = makeStyles()({
    expanded: { width: '380px' },
    shrinked: { minWidth: 36 }
})

function renderFilter(scope: IFilterScope, index: number) {
    const key = `${index}-${scope.caption}`
    if (scope instanceof StringFilterScope) return <TcmStringFilterView key={key} scope={scope} />
    if (scope instanceof StringKeywordFilterScope) return <TcmStringKeywordFilterView key={key} scope={scope} />
    if (scope instanceof MultipleOptionStringKeywordFilterScope) {
        return <StringMultiSelectFilterView key={key} scope={scope} />
    }
    if (scope instanceof NumberFilterScope) return <NumberFilterView key={key} scope={scope} />
    if (scope instanceof DateFilterScope) return <DateFilterView key={key} scope={scope} />
    if (scope instanceof SelectFilterScope) return <SelectFilterView key={key} scope={scope} />
    return null
}

type MvFilterPaneViewProps = {
    className?: string
    scope: MvFilterPaneScope
}

class MvFilterPaneViewClass implements FCClassContext<MvFilterPaneViewProps> {
    scope!: MvFilterPaneScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState({ scope }: MvFilterPaneViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    readonly emitToggleExpanded = () => this.scope.onToggleExpanded().catch(LOG.caught)
    readonly emitClearFilters = () => this.scope.onClearFilters().catch(LOG.caught)
    readonly emitApply = () => this.scope.onApply().catch(LOG.caught)
    readonly emitSearchFilterChanged = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onSearchFilterChanged(evt.target.value).catch(LOG.caught)

    render({ className, scope }: MvFilterPaneViewProps) {
        return (
            <FilterPaneContainer
                className={clsx(scope.expanded ? this.classes.expanded : this.classes.shrinked, className)}
            >
                {scope.expanded ? this.renderOpenFilter() : this.renderCloseFilter()}
            </FilterPaneContainer>
        )
    }

    renderOpenFilter() {
        const { scope } = this
        return (
            <>
                <FilterPaneHeader>
                    <FilterListIcon />
                    <PaneTitle>Filtros</PaneTitle>
                    <Tooltip title="Ocultar filtros">
                        <IconActionButton size="small" aria-label="Ocultar filtros" onClick={this.emitToggleExpanded}>
                            <KeyboardDoubleArrowRightIcon />
                        </IconActionButton>
                    </Tooltip>
                </FilterPaneHeader>
                <FilterBody>
                    {scope.catalogVisible && <FilterCatalogView scope={scope.catalog} />}

                    <Field>
                        <FieldLabel htmlFor="mvFilterSearch">Pesquisar filtros</FieldLabel>
                        <TextField
                            id="mvFilterSearch"
                            value={scope.searchFilterText}
                            onChange={this.emitSearchFilterChanged}
                            placeholder="Buscar por nome do filtro"
                            size="small"
                            sx={inputSx}
                            fullWidth
                        />
                    </Field>
                    {scope.filters.map((filter, index) => (filter.visible ? renderFilter(filter, index) : null))}
                </FilterBody>
                <ApplyBand>
                    <Button
                        sx={buttonSx()}
                        onClick={this.emitClearFilters}
                        disabled={!scope.isFiltering}
                        startIcon={<CleaningServicesIcon />}
                    >
                        Limpar filtros
                    </Button>
                    <Button sx={buttonSx('primary')} onClick={this.emitApply}>
                        Aplicar
                    </Button>
                </ApplyBand>
            </>
        )
    }

    renderCloseFilter() {
        return (
            <CollapsedPaneHeader>
                <Tooltip title="Mostrar filtros">
                    <IconActionButton size="small" aria-label="Mostrar filtros" onClick={this.emitToggleExpanded}>
                        <KeyboardDoubleArrowLeftIcon />
                    </IconActionButton>
                </Tooltip>
                {this.scope.isFiltering && (
                    <Tooltip title="Limpar filtros">
                        <IconActionButton size="small" aria-label="Limpar filtros" onClick={this.emitClearFilters}>
                            <CleaningServicesIcon />
                        </IconActionButton>
                    </Tooltip>
                )}
                <CollapsedPaneFilterIcon />
                <CollapsedPaneLabel>Filtros</CollapsedPaneLabel>
            </CollapsedPaneHeader>
        )
    }
}

export const MvFilterPaneView = classToFComponent(MvFilterPaneViewClass, React)
export default MvFilterPaneView
